/**
 * startup-prefetch — take the network off the click → greeting path.
 *
 * Prod forensics (2026-09-12) found the Daily **call-machine bundle**
 * (`https://c.daily.co/call-machine/versioned/<v>/static/call-machine-object-bundle.js`,
 * ~1MB) being fetched only AFTER the visitor clicked "Talk to me", together
 * with the avatar idle loop and poster. All three are knowable the moment the
 * widget renders: the bundle URL is a pure function of the pinned daily-js
 * version, and the avatar asset URLs arrive with `validate-client-key`. Every
 * byte fetched here is a byte the greeting no longer waits for.
 *
 * ## Why these exact loaders
 *
 * A prefetch only pays off if it lands on the SAME HTTP cache entry the real
 * load will ask for. Both halves are matched deliberately:
 *
 * - **Daily bundle** — daily-js has two loaders (`dist/daily-esm.js`): a
 *   `<script src>` one, used ONLY when `dailyConfig.avoidEval` is set, and the
 *   default, which `fetch()`es the bundle (mode `cors`, credentials
 *   `same-origin`) and evaluates the text with `Function()`. We never set
 *   `avoidEval`, so the click-time request is a CORS fetch, and
 *   `<link rel="preload" as="fetch" crossorigin="anonymous">` is the exact
 *   match. Live check (2026-09-12, Chrome): with `as="script"` the fetch went
 *   through the network stack again (a second resource entry, served from the
 *   HTTP cache), i.e. the preload was never consumed and Chrome logs
 *   "preloaded but not used" on every page view; with `as="fetch"` +
 *   `crossorigin` the fetch consumed the preload directly (no second entry).
 *   `as="fetch"` needs `connect-src c.daily.co` on the host page's CSP, which
 *   daily-js's own fetch at click time already needs — no new requirement.
 * - **Avatar loop + poster** — these are the `?cors=1` variants, and
 *   `AvatarVideo` loads them with `crossOrigin="anonymous"` precisely so a
 *   non-CORS load can never poison the SDK's cache key (2026-09-10: one
 *   no-CORS load of an `assets.floe.so` URL broke every later CORS load of it
 *   in that browser for a YEAR, showing the raw green backdrop on the
 *   customer's page). So they are warmed with a detached `<video>` / `Image`
 *   carrying `crossOrigin='anonymous'` BEFORE `src` — same destination, same
 *   CORS mode, same cache key. Deliberately NOT `<link rel="preload">`: `as`
 *   would have to be `video`/`fetch`, the first is unevenly supported and the
 *   second needs `connect-src` on the customer's CSP, which the `?cors=1`
 *   design exists to avoid.
 *
 * ## When it runs
 *
 * Only for the **website agent** — `websiteAgent === true`, asked positively.
 * (`!demoMode` would fail open for every mode added after it; that negation is
 * what made host-page capture silently wrong when this agent arrived.) Nothing
 * here runs for the demo or onboarding agents, and nothing runs before the SDK
 * has validated its client key.
 *
 * Three triggers, in the order a visitor meets them:
 *
 *   1. `preconnectVoiceOrigins()` at boot — DNS + TLS to `c.daily.co` only.
 *      Cheap, and the handshake is the part that cannot be parallelised later.
 *   2. `scheduleVoiceStartupPrefetch()` at boot, for EVERY visitor — so that
 *      whenever they do click, it feels fast. Deferred off the host page's own
 *      critical path: never before the page's `load` event, then behind
 *      `requestIdleCallback` (a `setTimeout` where that is missing), so it
 *      cannot compete with the customer's LCP. Skipped on a constrained
 *      connection (`navigator.connection.saveData`, `2g` / `slow-2g`): those
 *      visitors keep today's behaviour and pay only if they show intent.
 *   3. `prefetchVoiceStartupAssets()` on the first intent signal
 *      (`pointerover` / `pointerdown` / `focusin` anywhere in the widget's own
 *      container). A no-op once (2) has run — the page-scoped latch means
 *      nothing is ever fetched twice — and the fallback for the visitors (2)
 *      skipped, where hover-to-click still buys several hundred ms.
 *
 * The cost of (2) is real and deliberate: every page view downloads the
 * Daily bundle (~0.46MB on the wire, 1.8MB decoded) and, on an avatar-enabled
 * site, the idle loop (~3.7MB) and poster (~40KB) — see the PR that made this
 * call for the measured figures. The trade is bandwidth for a greeting that
 * starts the moment the visitor asks for it.
 *
 * No consent gate: none of this reads, writes or transmits identity — it is
 * three GETs for static assets the session is about to make anyway. (The
 * conversation open, the visitor token and the events bootstrap are the
 * identity-bearing calls, and they keep their own C3/C5 gates.)
 */

import DailyIframe from '@daily-co/daily-js';
import { warmMedia } from './avatar-intro-preload';
import { logger as defaultLogger, type Logger } from './logger';

/** Origin of Daily's call-machine CDN. */
export const DAILY_CDN_ORIGIN = 'https://c.daily.co';

/**
 * The bundle URL for the daily-js build we ship.
 *
 * Built from `DailyIframe.version()` rather than a literal: a pinned string
 * would silently start warming a DEAD url on the next `@daily-co/daily-js`
 * bump — the prefetch would 404, the click path would fetch the real bundle as
 * it does today, and nothing would ever fail loudly enough to notice. Returns
 * null if daily-js is unavailable or reports no version, in which case we
 * simply skip the preload.
 */
export function dailyCallMachineBundleUrl(): string | null {
  try {
    const version = DailyIframe?.version?.();
    if (typeof version !== 'string' || !/^[\w.\-]+$/.test(version)) return null;
    return `${DAILY_CDN_ORIGIN}/call-machine/versioned/${version}/static/call-machine-object-bundle.js`;
  } catch {
    return null;
  }
}

function appendLink(rel: string, href: string, attrs: Record<string, string>): void {
  try {
    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;
    // One per (rel, href): re-appending re-requests on some browsers and, for
    // preconnect, opens a second socket for nothing. Compared in JS rather than
    // through an attribute selector — the URL is not a literal and CSS.escape
    // is not universally present.
    const existing = head.querySelectorAll(`link[rel="${rel}"]`);
    for (let i = 0; i < existing.length; i++) {
      if (existing[i].getAttribute('href') === href) return;
    }
    const link = document.createElement('link');
    link.rel = rel;
    for (const [key, value] of Object.entries(attrs)) link.setAttribute(key, value);
    link.href = href;
    head.appendChild(link);
  } catch {
    /* a prefetch that cannot be installed is a prefetch we do without */
  }
}

/** DNS + TLS to Daily's CDN, so the click path pays only for bytes. */
export function preconnectVoiceOrigins(): void {
  const bundle = dailyCallMachineBundleUrl();
  if (!bundle) return;
  appendLink('preconnect', DAILY_CDN_ORIGIN, { crossorigin: 'anonymous' });
}

/**
 * Warm one `assets.floe.so` media URL on the SDK's OWN cache key.
 *
 * `url` MUST already be the `?cors=1` variant (`corsCacheKeyUrl`): warming the
 * plain URL would land on a different entry and buy nothing, and warming the
 * `?cors=1` one WITHOUT `crossOrigin` is the poisoning load the variant exists
 * to prevent. Hence `crossOrigin` is assigned before `src` on both elements,
 * mirroring `AvatarVideo`.
 */
function warmCorsMedia(url: string, kind: 'video' | 'image'): void {
  // One registry for every warm on the page: the approach-intro preloader warms
  // the same close loop, and two detached elements for one URL is a second
  // download on browsers that do not share a media buffer between them.
  void warmMedia(url, kind);
}

/** Page-scoped latch: the warm is a browser-cache effect, not per-instance. */
let prefetchDone = false;

export interface VoiceStartupPrefetchAssets {
  /** Already-`?cors=1` idle-loop URL, when the site has an avatar. */
  avatarIdleLoopUrl?: string;
  /** Already-`?cors=1` poster URL, when the site has an avatar. */
  avatarPosterUrl?: string;
}

/**
 * Fetch everything the first second of a voice session needs, ahead of the
 * click. Idempotent — the first call wins and later intent signals are no-ops.
 *
 * @returns true when this call performed the warm (i.e. assets are, or are
 *          becoming, cached). The SDK forwards it as the `prefetched` prop on
 *          VOICE_READY so the timing aggregate can tell a warmed startup from
 *          a cold one instead of guessing.
 */
export function prefetchVoiceStartupAssets(
  assets: VoiceStartupPrefetchAssets = {},
  logger: Logger = defaultLogger,
): boolean {
  if (prefetchDone) return false;
  prefetchDone = true;
  const bundle = dailyCallMachineBundleUrl();
  if (bundle) {
    appendLink('preload', bundle, { as: 'fetch', crossorigin: 'anonymous' });
  }
  if (assets.avatarIdleLoopUrl) warmCorsMedia(assets.avatarIdleLoopUrl, 'video');
  if (assets.avatarPosterUrl) warmCorsMedia(assets.avatarPosterUrl, 'image');
  try {
    logger.log(
      `[OnboardingSDK] ⏩ Prefetched voice startup assets (dailyBundle=${!!bundle}, ` +
        `avatarLoop=${!!assets.avatarIdleLoopUrl}, avatarPoster=${!!assets.avatarPosterUrl})`,
    );
  } catch {
    /* logging is not the point of this function */
  }
  return true;
}

/** True once {@link prefetchVoiceStartupAssets} has run on this page. */
export function voiceStartupAssetsPrefetched(): boolean {
  return prefetchDone;
}

/**
 * The avatar media worth warming, gated the same way the render layer gates it.
 *
 * `validate-client-key` today only ever pairs `enabled: false` with empty URLs
 * (floe-api's `resolveAgentAvatar` returns `AGENT_AVATAR_DEFAULTS` on every
 * off path), but the SDK's config type does not encode that, the render layer
 * (`websiteAgentAvatarEnabled`) already re-checks `enabled` rather than
 * trusting the URLs, and this bundle outlives any one floe-api build on the
 * CDN. So a disabled avatar warms nothing, whatever URLs ride beside it —
 * 3.7MB is not a download to make on the strength of an invariant that lives
 * in another package.
 *
 * `toCacheKeyUrl` is the SDK's `corsCacheKeyUrl`: the warm MUST land on the
 * `?cors=1` entry (see {@link warmCorsMedia}); it is injected rather than
 * imported so this module stays free of the React component that owns it.
 */
export function voiceStartupAssetsForAvatar(
  avatar:
    | { enabled?: boolean; idleLoopUrl?: string; posterUrl?: string }
    | null
    | undefined,
  toCacheKeyUrl: (url: string) => string,
): VoiceStartupPrefetchAssets {
  if (avatar?.enabled !== true) return {};
  return {
    ...(avatar.idleLoopUrl
      ? { avatarIdleLoopUrl: toCacheKeyUrl(avatar.idleLoopUrl) }
      : {}),
    ...(avatar.posterUrl
      ? { avatarPosterUrl: toCacheKeyUrl(avatar.posterUrl) }
      : {}),
  };
}

/** How long the idle callback may be deferred before it runs regardless. */
export const PREFETCH_IDLE_TIMEOUT_MS = 2_000;
/** The delay used where `requestIdleCallback` is missing (Safari < 18). */
export const PREFETCH_FALLBACK_DELAY_MS = 1_500;

/**
 * True when the visitor has asked for less data or is on a connection where a
 * speculative megabyte is a bad trade. `navigator.connection` is Chromium-only;
 * its absence means "no signal", never "constrained".
 */
export function connectionIsConstrained(): boolean {
  try {
    const connection = (navigator as any)?.connection;
    if (!connection) return false;
    if (connection.saveData === true) return true;
    const effectiveType = connection.effectiveType;
    return effectiveType === '2g' || effectiveType === 'slow-2g';
  } catch {
    return false;
  }
}

/**
 * Warm the startup assets for every visitor, off the host page's critical path.
 *
 * Order of deferrals: wait for the page's `load` event if it has not fired,
 * then yield to `requestIdleCallback` with a {@link PREFETCH_IDLE_TIMEOUT_MS}
 * ceiling (or a {@link PREFETCH_FALLBACK_DELAY_MS} timer where rIC is
 * missing), then run {@link prefetchVoiceStartupAssets}. `getAssets` is read
 * at run time, not at scheduling time, so the avatar URLs are whatever the SDK
 * holds when the warm actually happens.
 *
 * Skipped entirely — returning a no-op — when the page already prefetched or
 * the connection is constrained; the intent path stays armed for both.
 *
 * @returns a cancel function; the SDK calls it from `disconnect()` so a torn
 *          down instance never fires a fetch on a page it has left.
 */
export function scheduleVoiceStartupPrefetch(
  getAssets: () => VoiceStartupPrefetchAssets,
  logger: Logger = defaultLogger,
): () => void {
  if (prefetchDone) return () => {};
  if (connectionIsConstrained()) {
    try {
      logger.log(
        '[OnboardingSDK] ⏩ Voice startup prefetch deferred to intent (constrained connection)',
      );
    } catch {
      /* not the point */
    }
    return () => {};
  }

  let cancelled = false;
  let idleHandle: number | null = null;
  let timerHandle: ReturnType<typeof setTimeout> | null = null;
  let onLoad: (() => void) | null = null;

  const run = () => {
    idleHandle = null;
    timerHandle = null;
    if (cancelled) return;
    try {
      prefetchVoiceStartupAssets(getAssets(), logger);
    } catch {
      /* a warm that fails is a warm we do without */
    }
  };

  const whenIdle = () => {
    if (cancelled) return;
    try {
      const ric = (globalThis as any).requestIdleCallback;
      if (typeof ric === 'function') {
        idleHandle = ric(run, { timeout: PREFETCH_IDLE_TIMEOUT_MS });
        return;
      }
    } catch {
      /* fall through to the timer */
    }
    timerHandle = setTimeout(run, PREFETCH_FALLBACK_DELAY_MS);
  };

  try {
    if (typeof document !== 'undefined' && document.readyState !== 'complete') {
      onLoad = () => {
        onLoad = null;
        whenIdle();
      };
      window.addEventListener('load', onLoad, { once: true });
    } else {
      whenIdle();
    }
  } catch {
    /* no document/window to schedule against — the intent path remains */
  }

  return () => {
    cancelled = true;
    try {
      if (onLoad) window.removeEventListener('load', onLoad);
      if (idleHandle !== null) (globalThis as any).cancelIdleCallback?.(idleHandle);
      if (timerHandle !== null) clearTimeout(timerHandle);
    } catch {
      /* nothing left to cancel */
    }
    onLoad = null;
    idleHandle = null;
    timerHandle = null;
  };
}

/** Test seam — the module-level latch is per-page, not per-instance. */
export function resetVoiceStartupPrefetchForTests(): void {
  prefetchDone = false;
}
